import React from "react";
import RightworkSelect from "../../../components/shared/RightworkSelect";
import LoadSpinner from "../../../utilities/LoadSpinner";

const inputClass = "w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-violet-600 disabled:bg-slate-100";
const billingOptions = [
  { value: "setting_up", label: "Setting up" },
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
];

export default function LocationDetailsEditor({ location, companyId, groupOptions, isRightworkInternal, canEditContactDetails = false, onSave, onDirtyChange }) {
  const formId = React.useId();
  const [edits, setEdits] = React.useState({});
  const [saving, setSaving] = React.useState(false);
  const [status, setStatus] = React.useState("");
  const [error, setError] = React.useState("");
  const dirty = Object.keys(edits).length > 0;
  const dirtyKey = `${companyId}:${location.id}:location-profile`;
  React.useEffect(() => {
    onDirtyChange?.(dirtyKey, dirty);
  }, [onDirtyChange, dirtyKey, dirty]);
  React.useEffect(() => () => onDirtyChange?.(dirtyKey, false), [onDirtyChange, dirtyKey]);
  const value = (field) => Object.prototype.hasOwnProperty.call(edits, field) ? edits[field] : location[field] ?? "";
  const change = (field, next) => {
    setEdits((previous) => ({ ...previous, [field]: next }));
    setStatus("");
  };
  const billingStatus = value("onboarding_status") || "setting_up";
  const billingStatusClass = {
    active: "bg-emerald-100 text-emerald-700",
    inactive: "bg-slate-200 text-slate-600",
    setting_up: "bg-amber-100 text-amber-800",
  }[billingStatus] || "bg-slate-200 text-slate-600";
  const save = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    setStatus("");
    try {
      // Merge into the latest location so independent feature edits survive.
      const contactChanges = Object.fromEntries(
        ["public_phone_number", "public_location_address"]
          .filter((field) => canEditContactDetails && Object.prototype.hasOwnProperty.call(edits, field)
            && (edits[field] || null) !== (location[field] || null))
          .map((field) => [field, edits[field] || null])
      );
      const result = await onSave({ ...location, ...edits }, { contactChanges });
      if (result === false) {
        setError("Couldn't save location details. Please try again.");
      } else {
        setEdits({});
        setStatus("Location details saved.");
      }
    } catch (_) {
      setError("Couldn't save location details. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return <section className="mt-6">
    <h3 className="mb-3 text-sm font-semibold text-slate-900">Location details</h3>
    <form onSubmit={save}>
      <fieldset disabled={saving} className="grid gap-4" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 200px), 1fr))" }}>
        {[["store_name", "Location name"], ["latitude", "Latitude"], ["longitude", "Longitude"]].map(([field, label]) => (
          <div key={field} style={field === "store_name" ? { gridColumn: "1 / -1" } : undefined}>
            <label htmlFor={`${formId}-${field}`} className="mb-1.5 block text-xs font-medium text-slate-600">{label}</label>
            <input id={`${formId}-${field}`} className={inputClass} value={value(field)}
              type={field === "store_name" ? "text" : "number"} step="any"
              min={field === "latitude" ? -90 : field === "longitude" ? -180 : undefined}
              max={field === "latitude" ? 90 : field === "longitude" ? 180 : undefined}
              required={field === "store_name"} onChange={(event) => change(field, event.target.value)} />
          </div>
        ))}
        {[["public_location_address", "Address"], ["public_phone_number", "Phone number"]].map(([field, label]) => (
          <div key={field} style={{ gridColumn: "1 / -1" }}>
            <label htmlFor={`${formId}-${field}`} className="mb-1.5 block text-xs font-medium text-slate-600">{label}</label>
            <input id={`${formId}-${field}`} className={`${inputClass} read-only:bg-slate-50 read-only:text-slate-500`} value={value(field)} maxLength={240} readOnly={!canEditContactDetails}
              type={field === "public_phone_number" ? "tel" : "text"} onChange={(event) => change(field, event.target.value)} />
          </div>
        ))}
        <div style={{ gridColumn: "1 / -1" }}>
          <label id={`${formId}-group-label`} htmlFor={`${formId}-group`} className="mb-1.5 block text-xs font-medium text-slate-600">Store group</label>
          <RightworkSelect id={`${formId}-group`} disabled={saving} value={value("store_group") ?? ""}
            onChange={(event) => change("store_group", event.target.value === "" ? null : event.target.value)}
            options={[{ value: "", label: "No group" }, ...groupOptions]} />
          {groupOptions.length === 0 && <p className="mt-1 text-xs text-slate-500">Add regions or districts under Settings &gt; Store groups.</p>}
        </div>
        <div style={{ gridColumn: "1 / -1" }}>
          {isRightworkInternal ? <>
            <label id={`${formId}-billing-label`} htmlFor={`${formId}-billing`} className="mb-1.5 block text-xs font-medium text-slate-600">Billing status</label>
            <RightworkSelect id={`${formId}-billing`} disabled={saving} value={billingStatus}
              onChange={(event) => change("onboarding_status", event.target.value)} options={billingOptions} />
            <p className="mt-1 text-xs text-slate-500">Only active stores are billed. Changing this will affect the next billing cycle.</p>
          </> : <dl className="flex flex-wrap items-center justify-between gap-3 rounded-lg bg-slate-50 px-3 py-3">
            <dt className="text-xs font-medium text-slate-600">Billing status</dt>
            <dd className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${billingStatusClass}`}>
              <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-current" />
              {billingOptions.find((option) => option.value === billingStatus)?.label || billingStatus}
            </dd>
          </dl>}
        </div>
      </fieldset>
      {error && <p role="alert" className="mt-3 text-sm text-rose-700">{error}</p>}
      <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
        <div role="status" className="text-xs text-slate-500">{saving ? <><span className="sr-only">Saving location details</span><div aria-hidden="true"><LoadSpinner /></div></> : status}</div>
        <button disabled={saving || !dirty} className="rounded-lg bg-violet-700 px-5 py-2.5 text-sm font-semibold text-white hover:bg-violet-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-violet-600 disabled:opacity-50">Save location details</button>
      </div>
    </form>
  </section>;
}
